import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HiDotsVertical, HiPaperAirplane, HiReply, HiTrash, HiX } from 'react-icons/hi';
import { EventComment } from '../../../types';
import { useGeneral } from '../../../contexts';
import Services from '../../../services/Services';
import Card from '../../ui/Card/Card';
import Button from '../../ui/Button/Button';
import Modal from '../../ui/Modal/Modal';
import UserAvatar from '../../ui/UserDisplay/UserAvatar';
import UserProfileLink from '../../ui/UserDisplay/UserProfileLink';
import { formatDateOnly, formatTimeOnly } from '../../../utils/dateFormatters';
import styles from './EventCommentsSection.module.css';

interface EventCommentsSectionProps {
  token: string | null;
  channelID: string;
  eventID: string;
  comments: EventComment[];
  currentUserId: string | null;
  canPost: boolean;
  isChannelAdmin: boolean;
  onRefresh: () => void;
}

function toEpochSeconds(value: string | null | undefined): number | null {
  if (!value) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

const EventCommentsSection: React.FC<EventCommentsSectionProps> = ({
  token,
  channelID,
  eventID,
  comments,
  currentUserId,
  canPost,
  isChannelAdmin,
  onRefresh,
}) => {
  const { language, toastError, toastSuccess } = useGeneral();
  const { getTranslation, language: currentLanguage } = language;

  const [content, setContent] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [deletingCommentID, setDeletingCommentID] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<EventComment | null>(null);
  const [replyTo, setReplyTo] = useState<EventComment | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [openActionsCommentID, setOpenActionsCommentID] = useState<string | null>(null);
  const openActionsRef = useRef<HTMLDivElement | null>(null);

  const normalizedComments = useMemo(() => {
    return Array.isArray(comments) ? comments : [];
  }, [comments]);

  const canPerformActions = !!token;
  const canReply = canPost && canPerformActions;

  useEffect(() => {
    if (!openActionsCommentID) return;

    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      const node = openActionsRef.current;
      if (!node) return;
      if (node.contains(e.target as Node)) return;
      setOpenActionsCommentID(null);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown as any);
    };
  }, [openActionsCommentID]);

  const commentsById = useMemo(() => {
    const map = new Map<string, EventComment>();
    normalizedComments.forEach((c) => {
      const key = String(c.commentID || c.id || '');
      if (key) map.set(key, c);
    });
    return map;
  }, [normalizedComments]);

  const repliesByParentId = useMemo(() => {
    const map = new Map<string, EventComment[]>();
    normalizedComments.forEach((c) => {
      const parentIdRaw = (c as any)?.repliedCommentID as string | null | undefined;
      const parentId = parentIdRaw ? String(parentIdRaw) : '';
      if (!parentId) return;
      if (!map.has(parentId)) map.set(parentId, []);
      map.get(parentId)!.push(c);
    });
    return map;
  }, [normalizedComments]);

  const topLevelComments = useMemo(() => {
    // Top-level = not a reply OR replying to a comment that isn't present (orphan)
    return normalizedComments.filter((c) => {
      const parentIdRaw = (c as any)?.repliedCommentID as string | null | undefined;
      const parentId = parentIdRaw ? String(parentIdRaw) : '';
      if (!parentId) return true;
      return !commentsById.has(parentId);
    });
  }, [normalizedComments, commentsById]);

  const handleSubmit = async () => {
    if (!canPost || !canPerformActions) return;

    const trimmed = content.trim();
    if (!trimmed) {
      toastError(getTranslation('events.commentEmpty') || 'Please write a comment.');
      return;
    }

    setIsPosting(true);
    try {
      const res = await Services.SocialHiveCommentOnEvent(
        token!,
        channelID,
        eventID,
        trimmed,
        replyTo?.commentID ?? null
      );
      if (res.data?.status) {
        setContent('');
        setReplyTo(null);
        toastSuccess(getTranslation('events.commentPosted') || 'Comment posted.');
        onRefresh();
      } else {
        toastError(
          res.data?.message ||
            getTranslation('events.commentPostFailed') ||
            'Could not post comment.'
        );
      }
    } catch (err: any) {
      toastError(
        err?.response?.data?.message ||
          err?.message ||
          getTranslation('events.commentPostFailed') ||
          'Could not post comment.'
      );
    } finally {
      setIsPosting(false);
    }
  };

  const getCascadeDeleteOrder = (rootCommentId: string): string[] => {
    // Delete descendants first, then the root
    const childrenByParent = new Map<string, string[]>();
    normalizedComments.forEach((c) => {
      const parentIdRaw = (c as any)?.repliedCommentID as string | null | undefined;
      const parentId = parentIdRaw ? String(parentIdRaw) : '';
      if (!parentId) return;
      const childId = String(c.commentID || c.id || '');
      if (!childId) return;
      if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
      childrenByParent.get(parentId)!.push(childId);
    });

    const visited = new Set<string>();
    const order: string[] = [];

    const dfs = (id: string) => {
      if (!id || visited.has(id)) return;
      visited.add(id);
      const children = childrenByParent.get(id) || [];
      children.forEach(dfs);
      order.push(id);
    };

    dfs(String(rootCommentId));
    return order;
  };

  const handleDelete = async (comment: EventComment) => {
    if (!canPerformActions) return;
    setDeletingCommentID(comment.commentID);
    try {
      const deleteOrder = getCascadeDeleteOrder(comment.commentID);

      for (const commentID of deleteOrder) {
        const res = await Services.SocialHiveRemoveChannelEventComment(token!, channelID, eventID, commentID);
        if (!res.data?.status) {
          throw new Error(
            res.data?.message ||
              getTranslation('events.commentDeleteFailed') ||
              'Could not delete comment.'
          );
        }
      }

      toastSuccess(getTranslation('events.commentDeleted') || 'Comment deleted.');
      onRefresh();
      setConfirmDelete(null);
    } catch (err: any) {
      toastError(
        err?.response?.data?.message ||
          err?.message ||
          getTranslation('events.commentDeleteFailed') ||
          'Could not delete comment.'
      );
    } finally {
      setDeletingCommentID(null);
    }
  };

  const handleComposerKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    // Don't interfere with IME composition (e.g., Turkish/Japanese input)
    if ((e as any).isComposing) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      if (!canPerformActions || isPosting || content.trim().length === 0) return;
      void handleSubmit();
    }
  };

  const handleStartReply = (comment: EventComment) => {
    if (!canReply) return;
    setReplyTo(comment);
    setOpenActionsCommentID(null);
    // focus after state update
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const renderTimestamp = (comment: EventComment) => {
    const createdSeconds = toEpochSeconds(comment.createdDateTime);
    if (!createdSeconds) return '—';
    const date = formatDateOnly(createdSeconds, currentLanguage);
    const time = formatTimeOnly(createdSeconds);
    return `${date}, ${time}`;
  };

  const getFullName = (comment: EventComment) => {
    return `${comment.userInfos?.name || ''} ${comment.userInfos?.lastname || ''}`.trim() || 'User';
  };

  const getCommentKey = (comment: EventComment) => String(comment.commentID || comment.id);

  const renderCommentRow = (comment: EventComment, depth: number) => {
    const fullName = getFullName(comment);
    const showDelete = isChannelAdmin
    const isDeletingThis = deletingCommentID === comment.commentID;
    const indentPx = Math.min(depth, 3) * 28;
    const commentID = String(comment.commentID || comment.id);
    const isMenuOpen = openActionsCommentID === commentID;
    const hasActions = canReply || showDelete;

    return (
      <div
        key={getCommentKey(comment)}
        className={styles.commentRow}
        style={{ marginLeft: indentPx } as React.CSSProperties}
      >
        <UserProfileLink
          userId={comment.userInfos?.id || comment.userID}
          name={comment.userInfos?.name}
          lastname={comment.userInfos?.lastname}
        >
          <UserAvatar
            avatarUrl={comment.userInfos?.userAvatar ?? null}
            name={fullName}
            size={depth > 0 ? 32 : 36}
            className={styles.avatar}
          />
        </UserProfileLink>
        <div className={styles.commentBody}>
          <div className={styles.metaRow}>
            <div className={styles.nameLine}>
              <UserProfileLink
                userId={comment.userInfos?.id || comment.userID}
                name={comment.userInfos?.name}
                lastname={comment.userInfos?.lastname}
              >
                <span className={styles.name}>{fullName}</span>
              </UserProfileLink>
              <span className={styles.timeInline}>{renderTimestamp(comment)}</span>
            </div>
          </div>
          <div className={styles.content}>{comment.content}</div>
          <div className={styles.timeUnder}>{renderTimestamp(comment)}</div>
        </div>

        {hasActions && (
          <div className={styles.actions}>
            {canReply && (
              <button
                type="button"
                className={styles.replyButton}
                onClick={() => handleStartReply(comment)}
                disabled={!canPerformActions || isPosting}
                aria-label={getTranslation('events.reply') || 'Reply'}
                title={getTranslation('events.reply') || 'Reply'}
              >
                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                {/* @ts-ignore - react-icons compatibility with React 19 */}
                <HiReply className={styles.replyIcon} />
              </button>
            )}

            {showDelete && (
              <button
                type="button"
                className={styles.deleteButton}
                onClick={() => setConfirmDelete(comment)}
                disabled={!canPerformActions || isDeletingThis}
                aria-label={getTranslation('common.delete') || 'Delete'}
              >
                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                {/* @ts-ignore - react-icons compatibility with React 19 */}
                <HiTrash className={styles.deleteIcon} />
              </button>
            )}
          </div>
        )}

        {hasActions && (
          <div
            className={styles.mobileActions}
            ref={(node) => {
              if (isMenuOpen) openActionsRef.current = node;
            }}
          >
            <button
              type="button"
              className={styles.mobileActionsButton}
              onClick={() => setOpenActionsCommentID((prev) => (prev === commentID ? null : commentID))}
              aria-label={getTranslation('common.actions') || 'Actions'}
              title={getTranslation('common.actions') || 'Actions'}
            >
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiDotsVertical className={styles.mobileActionsIcon} />
            </button>

            {isMenuOpen && (
              <div className={styles.mobileActionsMenu} role="menu">
                {canReply && (
                  <button
                    type="button"
                    className={styles.mobileActionsItem}
                    onClick={() => handleStartReply(comment)}
                    role="menuitem"
                  >
                    {getTranslation('events.reply') || 'Reply'}
                  </button>
                )}
                {showDelete && (
                  <button
                    type="button"
                    className={styles.mobileActionsItemDanger}
                    onClick={() => {
                      setOpenActionsCommentID(null);
                      setConfirmDelete(comment);
                    }}
                    disabled={!canPerformActions || isDeletingThis}
                    role="menuitem"
                  >
                    {getTranslation('common.delete') || 'Delete'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderReplies = (parentId: string, depth: number): React.ReactNode => {
    const replies = repliesByParentId.get(parentId) || [];
    if (replies.length === 0) return null;
    return replies.map((reply) => (
      <React.Fragment key={getCommentKey(reply)}>
        {renderCommentRow(reply, depth)}
        {renderReplies(String(reply.commentID), depth + 1)}
      </React.Fragment>
    ));
  };

  const replyToName = replyTo ? getFullName(replyTo) : '';

  return (
    <div className={styles.root}>
      <Card className={styles.card}>
        <div className={styles.header}>
          <h2 className={styles.title}>{getTranslation('events.comments') || 'Comments'}</h2>
        </div>

        {normalizedComments.length === 0 ? (
          <div className={styles.emptyState}>
            {getTranslation('events.noCommentsYet') || 'No comments yet.'}
          </div>
        ) : (
          <div className={styles.list}>
            {topLevelComments.map((comment) => (
              <React.Fragment key={getCommentKey(comment)}>
                {renderCommentRow(comment, 0)}
                {renderReplies(String(comment.commentID), 1)}
              </React.Fragment>
            ))}
          </div>
        )}

        {canPost && (
          <div className={styles.form}>
            <div className={styles.composerRow}>
              <div className={styles.composerInputStack}>
                {replyTo && (
                  <div className={styles.replyBanner}>
                    <div className={styles.replyBannerText}>
                      {getTranslation('events.replyingTo') || 'Replying to'}{' '}
                      <span className={styles.replyBannerName}>{replyToName}</span>
                    </div>
                    <button
                      type="button"
                      className={styles.cancelReplyButton}
                      onClick={() => setReplyTo(null)}
                      aria-label={getTranslation('events.cancelReply') || 'Cancel reply'}
                      title={getTranslation('events.cancelReply') || 'Cancel reply'}
                    >
                      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiX className={styles.cancelReplyIcon} />
                    </button>
                  </div>
                )}
                <textarea
                  ref={textareaRef}
                  className={styles.composerTextarea}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  onKeyDown={handleComposerKeyDown}
                  placeholder={getTranslation('events.writeComment') || 'Write a comment...'}
                  disabled={!canPerformActions || isPosting}
                  rows={1}
                />
              </div>
              <Button
                variant="primary"
                size="small"
                onClick={() => void handleSubmit()}
                disabled={!canPerformActions || isPosting || content.trim().length === 0}
                className={styles.sendButton}
              >
                <span className={styles.srOnly}>
                  {isPosting ? (getTranslation('common.loading') || 'Posting...') : (getTranslation('common.send') || 'Send')}
                </span>
                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                {/* @ts-ignore - react-icons compatibility with React 19 */}
                <HiPaperAirplane className={styles.sendIcon} />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Modal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        variant="confirm"
        title={getTranslation('common.delete') || 'Delete'}
        message={getTranslation('events.confirmDeleteComment') || 'Delete this comment?'}
        messageClassName={styles.centeredModalMessage}
        confirmText={getTranslation('common.delete') || 'Delete'}
        cancelText={getTranslation('common.cancel') || 'Cancel'}
        onConfirm={() => confirmDelete && void handleDelete(confirmDelete)}
        confirmVariant="primary"
        isDanger
      />
    </div>
  );
};

export default EventCommentsSection;

